import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const createApiClient = () => {
    const client = axios.create({
        baseURL: `${API_URL}/api`,
    });

    client.interceptors.request.use((config) => {
        const token = localStorage.getItem('eskrybe_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    client.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401) {
                // Don't redirect for admin/affiliate endpoints - they have their own auth
                const url = error.config?.url || '';
                if (!url.includes('/admin/') && !url.includes('/affiliate/')) {
                    localStorage.removeItem('eskrybe_token');
                    window.location.href = '/login';
                }
            }
            return Promise.reject(error);
        }
    );

    return client;
};

export const api = createApiClient();

// Auth
export const authApi = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    me: () => api.get('/auth/me'),
};

// Books
export const booksApi = {
    list: () => api.get('/books'),
    get: (id) => api.get(`/books/${id}`),
    create: (data) => api.post('/books', data),
    update: (id, data) => api.put(`/books/${id}`, data),
    delete: (id) => api.delete(`/books/${id}`),
    stats: (id) => api.get(`/stats/${id}`),
    exportEpub: (id) => api.get(`/export/epub/${id}`, { responseType: 'blob' }),
    exportDocx: (id) => api.get(`/export/docx/${id}`, { responseType: 'blob' }),
};

// Chapters
export const chaptersApi = {
    list: (bookId) => api.get(`/chapters/${bookId}`),
    create: (data) => api.post('/chapters', data),
    update: (id, data) => api.put(`/chapters/${id}`, data),
    delete: (id) => api.delete(`/chapters/${id}`),
};

// Scenes
export const scenesApi = {
    list: (chapterId) => api.get(`/scenes/${chapterId}`),
    create: (data) => api.post('/scenes', data),
    update: (id, data) => api.put(`/scenes/${id}`, data),
    delete: (id) => api.delete(`/scenes/${id}`),
};

// Characters
export const charactersApi = {
    list: (bookId) => api.get(`/characters/${bookId}`),
    create: (data) => api.post('/characters', data),
    update: (id, data) => api.put(`/characters/${id}`, data),
    delete: (id) => api.delete(`/characters/${id}`),
};

// Locations (Worldbuilding)
export const locationsApi = {
    list: (bookId) => api.get(`/locations/${bookId}`),
    create: (data) => api.post('/locations', data),
    update: (id, data) => api.put(`/locations/${id}`, data),
    delete: (id) => api.delete(`/locations/${id}`),
};

// Plot Notes
export const plotNotesApi = {
    list: (bookId) => api.get(`/plot-notes/${bookId}`),
    create: (data) => api.post('/plot-notes', data),
    update: (id, data) => api.put(`/plot-notes/${id}`, data),
    delete: (id) => api.delete(`/plot-notes/${id}`),
};

// Timeline
export const timelineApi = {
    list: (bookId) => api.get(`/timeline/${bookId}`),
    create: (data) => api.post('/timeline', data),
    update: (id, data) => api.put(`/timeline/${id}`, data),
    delete: (id) => api.delete(`/timeline/${id}`),
};

// AI
export const aiApi = {
    write: (data) => api.post('/ai/write', data),
    illustrate: (data) => api.post('/ai/illustrate', data),
    illustrations: () => api.get('/illustrations'),
    getIllustrations: (bookId) => api.get(`/illustrations?book_id=${bookId}`),
};

// Credits
export const creditsApi = {
    packages: () => api.get('/credits/packages'),
    balance: () => api.get('/credits/balance'),
    checkout: (data) => api.post('/credits/checkout', data),
    status: (sessionId) => api.get(`/credits/status/${sessionId}`),
};

// Subscriptions
export const subscriptionsApi = {
    tiers: () => api.get('/subscriptions/tiers'),
};

// Blog
export const blogApi = {
    list: (publishedOnly = true) => api.get(`/blog?published_only=${publishedOnly}`),
    get: (id) => api.get(`/blog/${id}`),
    create: (data) => api.post('/blog', data),
};

// Resources
export const resourcesApi = {
    list: (category) => api.get(category ? `/resources?category=${category}` : '/resources'),
    create: (data) => api.post('/resources', data),
};

// Academy
export const academyApi = {
    lessons: () => api.get('/academy/lessons'),
    lesson: (id) => api.get(`/academy/lessons/${id}`),
    modules: () => api.get('/academy/modules'),
    generateVideo: (lessonId) => api.post(`/academy/generate-video/${lessonId}`),
};

// Writing Streak
export const writingApi = {
    logSession: (data) => api.post('/writing/log', data),
    getStreak: () => api.get('/writing/streak'),
    getHistory: (days = 30) => api.get(`/writing/history?days=${days}`),
    getGoals: () => api.get('/writing/goals'),
    updateGoals: (data) => api.put('/writing/goals', data),
};

// Document Upload
export const documentsApi = {
    upload: (file, onProgress) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/documents/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: onProgress,
        });
    },
    createBook: (uploadId, title, genre) => 
        api.post(`/documents/${uploadId}/create-book?${title ? `title=${encodeURIComponent(title)}` : ''}${genre ? `&genre=${encodeURIComponent(genre)}` : ''}`),
    getUploads: () => api.get('/documents/uploads'),
};

// Payments API
export const paymentsApi = {
    createSubscriptionCheckout: (planId, originUrl) => 
        api.post(`/payments/checkout/subscription?plan_id=${planId}&origin_url=${encodeURIComponent(originUrl)}`),
    createCreditsCheckout: (packageId, originUrl) => 
        api.post(`/payments/checkout/credits?package_id=${packageId}&origin_url=${encodeURIComponent(originUrl)}`),
    getCheckoutStatus: (sessionId) => 
        api.get(`/payments/checkout/status/${sessionId}`),
    getPlans: () => api.get('/payments/plans'),
    getCreditPackages: () => api.get('/payments/credit-packages'),
};

// Promotions API
export const promotionsApi = {
    getServices: () => api.get('/promotions/services'),
    getService: (id) => api.get(`/promotions/services/${id}`),
    getCampaigns: () => api.get('/promotions/campaigns'),
    getCampaign: (id) => api.get(`/promotions/campaigns/${id}`),
    createCampaign: (data) => api.post('/promotions/campaigns', data),
    updateCampaign: (id, data) => api.put(`/promotions/campaigns/${id}`, data),
    deleteCampaign: (id) => api.delete(`/promotions/campaigns/${id}`),
    updateSubmission: (id, data) => api.put(`/promotions/submissions/${id}`, data),
    getStats: () => api.get('/promotions/stats'),
    getRecommendations: (bookId) => api.get(`/promotions/recommendations/${bookId}`),
};

// Covers API
export const coversApi = {
    getPresets: () => api.get('/covers/presets'),
    getDimensions: () => api.get('/covers/dimensions'),
    getCosts: () => api.get('/covers/costs'),
    generate: (data) => api.post('/covers/generate', data),
    generateVariation: (data) => api.post('/covers/generate/variation', data),
    getBookCovers: (bookId) => api.get(`/covers/book/${bookId}`),
    getLibrary: () => api.get('/covers/library'),
    delete: (id) => api.delete(`/covers/${id}`),
    setPrimary: (id) => api.put(`/covers/${id}/set-primary`),
};

// Affiliate API (public tracking endpoints + affiliate portal)
export const affiliateApi = {
    // Public tracking (no auth required)
    trackClick: (refCode, source = 'direct', campaign = '') => 
        api.post(`/affiliate/track/click?ref_code=${refCode}&source=${encodeURIComponent(source)}&campaign=${encodeURIComponent(campaign)}`),
    trackSignup: (refCode, userId, userEmail) => 
        api.post(`/affiliate/track/signup?ref_code=${refCode}&user_id=${userId}&user_email=${encodeURIComponent(userEmail)}`),
    trackConversion: (userId, amount, isFirstPayment = true) => 
        api.post(`/affiliate/track/conversion?user_id=${userId}&payment_amount=${amount}&is_first_payment=${isFirstPayment}`),
    
    // Affiliate auth
    signup: (data) => api.post('/affiliate/signup', data),
    login: (data) => api.post('/affiliate/login', data),
    logout: (sessionId) => api.post(`/affiliate/logout?session_id=${sessionId}`),
    
    // Affiliate portal (session-based)
    getDashboard: (sessionId) => api.get(`/affiliate/dashboard?session_id=${sessionId}`),
    getProfile: (sessionId) => api.get(`/affiliate/profile?session_id=${sessionId}`),
    updateProfile: (sessionId, data) => api.put(`/affiliate/profile?session_id=${sessionId}`, data),
    getLink: (sessionId) => api.get(`/affiliate/link?session_id=${sessionId}`),
    createCustomLink: (sessionId, campaign, source) => 
        api.post(`/affiliate/link/custom?session_id=${sessionId}&campaign=${encodeURIComponent(campaign)}&source=${encodeURIComponent(source)}`),
    getLinks: (sessionId) => api.get(`/affiliate/links?session_id=${sessionId}`),
    getPayouts: (sessionId) => api.get(`/affiliate/payouts?session_id=${sessionId}`),
    requestPayout: (sessionId, data) => api.post(`/affiliate/payouts/request?session_id=${sessionId}`, data),
    cancelPayoutRequest: (sessionId, requestId) => api.delete(`/affiliate/payouts/request/${requestId}?session_id=${sessionId}`),
    getAnalytics: (sessionId, period = '30d') => api.get(`/affiliate/analytics?session_id=${sessionId}&period=${period}`),
    getReferrals: (sessionId, status = '') => api.get(`/affiliate/referrals?session_id=${sessionId}${status ? `&status=${status}` : ''}`),
};

// Admin API
export const adminApi = {
    // Auth
    login: (data) => api.post('/admin/login', data),
    logout: (sessionId) => api.post(`/admin/logout?session_id=${sessionId}`),
    
    // Dashboard
    getDashboard: (sessionId) => api.get(`/admin/dashboard?session_id=${sessionId}`),
    
    // User Management
    getUsers: (sessionId, skip = 0, limit = 50) => api.get(`/admin/users?session_id=${sessionId}&skip=${skip}&limit=${limit}`),
    getUser: (sessionId, userId) => api.get(`/admin/users/${userId}?session_id=${sessionId}`),
    updateUser: (sessionId, userId, data) => api.patch(`/admin/users/${userId}?session_id=${sessionId}`, data),
    addCredits: (sessionId, userId, credits) => api.post(`/admin/users/${userId}/add-credits?session_id=${sessionId}&credits=${credits}`),
    closeAccount: (sessionId, userId) => api.post(`/admin/users/${userId}/close?session_id=${sessionId}`),
    setAccountIdle: (sessionId, userId) => api.post(`/admin/users/${userId}/idle?session_id=${sessionId}`),
    reactivateAccount: (sessionId, userId) => api.post(`/admin/users/${userId}/reactivate?session_id=${sessionId}`),
    
    // Demo User Management
    setDemoStatus: (sessionId, userId, isDemo, durationDays = 14) => 
        api.patch(`/admin/users/${userId}?session_id=${sessionId}`, { 
            is_demo: isDemo, 
            demo_duration_days: durationDays 
        }),
    createDemoAccount: (sessionId, data) => api.post(`/admin/demo-accounts?session_id=${sessionId}`, data),
    createUser: (sessionId, data) => api.post(`/admin/users?session_id=${sessionId}`, data),
    getDemoAccounts: (sessionId) => api.get(`/admin/demo-accounts?session_id=${sessionId}`),
    deleteDemoAccount: (sessionId, userId) => api.delete(`/admin/demo-accounts/${userId}?session_id=${sessionId}`),
    
    // Affiliate Management
    getAffiliates: (sessionId) => api.get(`/admin/affiliates?session_id=${sessionId}`),
    getAffiliate: (sessionId, affiliateId) => api.get(`/admin/affiliates/${affiliateId}?session_id=${sessionId}`),
    updateAffiliate: (sessionId, affiliateId, data) => api.patch(`/admin/affiliates/${affiliateId}?session_id=${sessionId}`, data),
    messageAffiliate: (sessionId, affiliateId, subject, message) => 
        api.post(`/admin/affiliates/${affiliateId}/message?session_id=${sessionId}&subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`),
    recordPayout: (sessionId, affiliateId, amount, method = 'paypal') => 
        api.post(`/admin/affiliates/${affiliateId}/payout?session_id=${sessionId}&amount=${amount}&method=${method}`),
    
    // Payout Requests
    getPayoutRequests: (sessionId, status = 'pending') => api.get(`/admin/payout-requests?session_id=${sessionId}&status=${status}`),
    approvePayoutRequest: (sessionId, requestId, notes = '') => 
        api.post(`/admin/payout-requests/${requestId}/approve?session_id=${sessionId}&notes=${encodeURIComponent(notes)}`),
    rejectPayoutRequest: (sessionId, requestId, reason = '') => 
        api.post(`/admin/payout-requests/${requestId}/reject?session_id=${sessionId}&reason=${encodeURIComponent(reason)}`),
};

// Promo Codes
export const promoApi = {
    validate: (code) => api.post('/promo/validate', { code }),
    apply: (code) => api.post('/promo/apply', { code }),
    getMyPromo: () => api.get('/promo/my-promo'),
};

export default api;
